/**
 * Recursively traverses a QuestionnaireResponse and removes answers
 * from items that are not currently enabled according to their
 * `enableWhen` conditions in the Questionnaire definition.
 *
 * - Keeps the full item structure intact (does not drop questions).
 * - Clears out `answer` arrays for disabled questions so stale values
 *   are not persisted or submitted.
 * - Processes nested items recursively.
 *
 * @param items - The Questionnaire definition items (questions).
 * @param responseItems - The current QuestionnaireResponse items with answers.
 * @param response - The full QuestionnaireResponse for context when evaluating enablement.
 * @returns A new array of QuestionnaireResponseItems with disabled answers removed.
 */

import {
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';
import { isQuestionEnabled } from '@medplum/react';

export function pruneDisabledAnswers(
  items: QuestionnaireItem[] | undefined,
  responseItems: QuestionnaireResponseItem[] | undefined,
  response: QuestionnaireResponse,
  parentResponseItem?: QuestionnaireResponseItem
): QuestionnaireResponseItem[] | undefined {
  if (!items || !responseItems) {
    return responseItems;
  }

  return responseItems
    .map((respItem) => {
      const qItem = items.find((i) => i.linkId === respItem.linkId);
      if (!qItem) {
        return respItem;
      }

      // For repeatable groups, create a context response that includes the current group's data
      // This ensures enableWhen conditions are evaluated based on the individual group's answers
      const contextResponse = parentResponseItem
        ? {
            ...response,
            item: [parentResponseItem], // Use the parent group's response as context
          }
        : response;

      const enabled = isQuestionEnabled(qItem, contextResponse);

      return {
        ...respItem,
        // If not enabled → wipe out answer, but keep item structure
        answer: enabled ? respItem.answer : undefined,
        // Recurse into child items, passing the current response item as parent context
        item: pruneDisabledAnswers(
          qItem.item,
          respItem.item,
          response,
          respItem
        ),
      };
    })
    .filter((respItem) => {
      // For repeatable groups that are disabled, completely remove them from the response
      const qItem = items.find((i) => i.linkId === respItem.linkId);
      if (!qItem?.repeats) {
        return true; // Keep non-repeatable items or items without definition
      }

      // Check if this repeatable group is enabled
      const contextResponse = parentResponseItem
        ? {
            ...response,
            item: [parentResponseItem],
          }
        : response;

      const enabled = isQuestionEnabled(qItem, contextResponse);
      return enabled; // Only keep enabled repeatable groups
    });
}
